/** @jsx jsx */
import { jsx } from '@emotion/core';
import { Text, withSitecoreContext } from '@sitecore-jss/sitecore-jss-react';
import PropTypes from 'prop-types';
import defaultsDeep from 'lodash/defaultsDeep';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import compose from 'lodash/fp/compose';
import { withNamespaces } from 'react-i18next';
import { Container, Row, Column } from '../../../styles/Grid';
import { Breakpoints } from '../../../styles/Grid/GridSizes';
import Spacer from '../../ui/Spacer';
import { MediaQueries } from '../../../styles/Grid';
import DotComponent from '../../ui/DotComponent';
import Picture from '../../ui/Picture';
import GalleryCarousel from '../../ui/GalleryCarousel';
import { getshowStyles } from '../../../utils/responsive';
import { CLOUDINARY_BASE_VALUES } from '../../../utils/constants';
import { componentHasStickyLabel } from '../StickyBar';
import { SafePureComponent } from '../../../utils/SafePureComponent';
import {
  headlineColCss,
  contentBlockCss,
  headlineCss,
  bodyTextCss,
  containerMarginCss,
  upperHeadlineCss,
  amenityItemCss,
  featuresColumnCss,
  featuresListCss,
  previewImageStyleIcon,
  iconContainerCss,
  imageRightCss,
  spacerCss,
} from './index.styles';

export const defaultData = {
  UpperHeadline: { value: '' },
  Headline: { value: '' },
  ContentText: { value: '' },
  FlipImage: { value: false },
  ListOfFeatures: [],
  Images: {
    value: [],
  },
};

const imageSizes = {
  [Breakpoints.lg]: {
    ...CLOUDINARY_BASE_VALUES,
    width: 404,
    height: 539,
    gravity: 'auto:subject',
    aspectRatio: '3:4',
  },
  [Breakpoints.md]: {
    ...CLOUDINARY_BASE_VALUES,
    width: 327,
    height: 436,
    gravity: 'auto:subject',
    aspectRatio: '3:4',
  },
};

const iconImageSizes = {
  [Breakpoints.xl]: {
    ...CLOUDINARY_BASE_VALUES,
    aspectRatio: '1:1',
    width: 18,
    height: 18,
  },
  [Breakpoints.lg]: {
    ...CLOUDINARY_BASE_VALUES,
    aspectRatio: '1:1',
    width: 18,
    height: 18,
  },
  [Breakpoints.md]: {
    ...CLOUDINARY_BASE_VALUES,
    aspectRatio: '1:1',
    width: 18,
    height: 18,
  },
  [Breakpoints.sm]: {
    ...CLOUDINARY_BASE_VALUES,
    aspectRatio: '1:1',
    width: 18,
    height: 18,
  },
};

export class ImageAndDescriptionWithItemList extends SafePureComponent {
  safeRender() {
    const { props } = this;
    const fields = defaultsDeep(props.fields, defaultData);
    const hasStickyLabel = componentHasStickyLabel(props);
    const {
      UpperHeadline,
      Headline,
      ContentText,
      ListOfFeatures,
      Images,
      FlipImage,
    } = fields;

    return (
      <Spacer
        marginCss={containerMarginCss}
        containerProps={{
          'data-sticky-id': hasStickyLabel && get(props, 'rendering.uid'),
        }}
      >
        <Container>
          <Row>
            <Column
              css={[
                FlipImage.value && imageRightCss,
                getshowStyles(MediaQueries.mdAndUp, 'inline-block'),
              ]}
              cols={{ sm: 0, md: 4, lg: 4 }}
              offsets={{ lg: 1 }}
            >
              <GalleryCarousel images={Images.value} imageSizes={imageSizes} />
            </Column>
            <Column
              cols={{ sm: 6, md: 4, lg: 5 }}
              offsets={{ lg: 1 }}
              css={contentBlockCss}
            >
              <Row>
                <Column css={headlineColCss}>
                  <p css={upperHeadlineCss}>
                    <Text field={UpperHeadline} />
                  </p>
                  <h2 css={headlineCss}>
                    <Text field={Headline} />
                    <DotComponent />
                  </h2>
                </Column>
              </Row>
              <Row>
                <Column>
                  <p css={[bodyTextCss, isEmpty(ListOfFeatures) && spacerCss]}>
                    <Text field={ContentText} />
                  </p>
                </Column>
              </Row>
              <Row>
                <Column
                  css={[
                    featuresColumnCss,
                    !isEmpty(ListOfFeatures) && spacerCss,
                  ]}
                >
                  <ul css={featuresListCss}>
                    {!isEmpty(ListOfFeatures) &&
                      ListOfFeatures.map((feature, index) => (
                        <li css={amenityItemCss} key={`amenities-${index}`}>
                          {!!get(feature, 'fields.Icon') && (
                            <span css={iconContainerCss}>
                              <Picture
                                pictureCss={previewImageStyleIcon}
                                image={feature.fields.Icon}
                                imageSizes={iconImageSizes}
                                editable={false}
                              />
                            </span>
                          )}
                          {!!get(feature, 'fields.Label') && (
                            <span>
                              <Text
                                field={feature.fields.Label}
                                editable={false}
                              />
                            </span>
                          )}
                        </li>
                      ))}
                  </ul>
                </Column>
              </Row>
            </Column>
          </Row>
        </Container>
      </Spacer>
    );
  }
}

ImageAndDescriptionWithItemList.displayName = 'ImageAndDescriptionWithItemList';

ImageAndDescriptionWithItemList.defaultProps = {
  fields: defaultData,
  t: key => key,
};

ImageAndDescriptionWithItemList.propTypes = {
  fields: PropTypes.shape({
    Headline: PropTypes.shape({
      value: PropTypes.string,
    }),
    ContentText: PropTypes.shape({
      value: PropTypes.string,
    }),
    ListOfFeatures: PropTypes.arrayOf(
      PropTypes.shape({
        fields: PropTypes.shape({
          Label: PropTypes.shape({
            value: PropTypes.string,
          }),
          Icon: PropTypes.shape({
            value: PropTypes.shape({
              src: PropTypes.string,
              alt: PropTypes.string,
            }),
          }),
        }),
      }),
    ),
    Images: PropTypes.shape({
      value: PropTypes.arrayOf(
        PropTypes.shape({
          value: PropTypes.shape({
            src: PropTypes.string,
            alt: PropTypes.string,
            caption: PropTypes.string,
          }),
        }),
      ),
    }),
  }),
};

export default compose(
  withSitecoreContext(),
  withNamespaces(),
)(ImageAndDescriptionWithItemList);
